/**
 * Typed web API client (architecture.md §5: `api/client.ts` — fetch-обёртка,
 * добавляет session_token, нормализует ответ в `ApiResult<T>`).  E10-F01-T04.
 *
 * This is the cross-package seam: the web SPA speaks the SAME contracts as the
 * Fastify API by importing them from `@eer/shared`.
 *
 * Legacy contract preserved (task E10-F01-T04): the old SPA sent everything
 * through one `apiCall(command, payload)` that POSTed
 * `{ command, session_token, ...payload }` to the n8n webhook. The v2 transport
 * is clean REST (`Authorization: Bearer <session_token>`), but this client keeps
 * the same *logical* contract — a single typed entry point that:
 *   - auto-injects the `session_token` from the session store on every request
 *     (Bearer header for JSON; appended into the FormData for multipart, so a
 *     file upload still carries `command` + `session_token` in the body — the
 *     exact legacy `payload.append('command'|'session_token', …)` behaviour);
 *   - accepts both a JSON body and a `FormData` (multipart) body for uploads;
 *   - normalizes every success body to one shape via `unwrapLegacySuccess`
 *     (array `[{response}]`, `{response}` wrapper, and a bare object all collapse
 *     to the same data);
 *   - NEVER throws on a network/parse failure — it returns
 *     `{ ok:false, error }` so UI/feature code branches on a value, not a catch.
 */
import {
  ok,
  err,
  apiErrorDetailSchema,
  unwrapLegacySuccess,
  isLegacyErrorPayload,
  normalizeLegacyError,
  normalizeN8nPayload,
  isUnauthorizedError,
  isPasswordResetRequiredDetails,
  type ApiResult,
  type ApiErrorDetail,
  type LegacyEnvelope,
} from '@eer/shared';
import { getSessionToken } from './session.js';
import { handleUnauthorized, handlePasswordResetRequired } from '../auth/unauthorized.js';

/**
 * E12-F08-T13: is this a 403 from the API's password-reset gate
 * (`error.code = 'FORBIDDEN'`, `error.details.reason = 'password_reset_required'`)?
 */
function isPasswordResetGate(status: number, detail: ApiErrorDetail): boolean {
  return (
    status === 403 && detail.code === 'FORBIDDEN' && isPasswordResetRequiredDetails(detail.details)
  );
}

const UNKNOWN_ERROR: ApiErrorDetail = {
  code: 'INTERNAL',
  message: 'Unexpected error',
};

const NETWORK_ERROR: ApiErrorDetail = {
  code: 'INTERNAL',
  message: 'Network unavailable. Check your connection and try again.',
};

/**
 * Base URL of the REST API. Configured at runtime via `VITE_API_BASE_URL`
 * (architecture.md §5 "конфиг через env на рантайме"); defaults to a same-origin
 * `/api` so a single deployed bundle works without rebuild.
 */
const API_BASE_URL: string = (import.meta.env?.VITE_API_BASE_URL as string | undefined) ?? '/api';

/** HTTP verbs the client issues (REST parity, architecture.md §4). */
export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

/** Options for a single request. `body` is JSON-serialised unless it is FormData. */
export interface RequestOptions {
  readonly method?: HttpMethod;
  /** JSON-serialisable payload, or a `FormData` for multipart/file uploads. */
  readonly body?: unknown;
  /** Extra query params appended to the URL. */
  readonly query?: Record<string, string | number | boolean | undefined>;
  /** Extra headers (merged over the defaults; never overrides auth unless set). */
  readonly headers?: Record<string, string>;
  /** Abort signal for cancellation (wired by TanStack Query). */
  readonly signal?: AbortSignal;
  /**
   * Let the request outlive the page (`fetch` keepalive) — used to flush a
   * pending auto-save from `pagehide` (reload / tab close). Body must stay < 64 KB.
   */
  readonly keepalive?: boolean;
  /**
   * Legacy `command` label. When `body` is a `FormData`, the command and the
   * session_token are appended into it (parity with the old upload contract);
   * for JSON requests it is sent as a header for tracing only.
   */
  readonly command?: string;
}

/** Build a full URL from a path + optional query params. */
function buildUrl(path: string, query?: RequestOptions['query']): string {
  const base = path.startsWith('http')
    ? path
    : `${API_BASE_URL}${path.startsWith('/') ? '' : '/'}${path}`;
  if (!query) return base;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined) params.append(k, String(v));
  }
  const qs = params.toString();
  return qs ? `${base}${base.includes('?') ? '&' : '?'}${qs}` : base;
}

/** True when the body is a multipart payload (file upload). */
function isFormData(body: unknown): body is FormData {
  return typeof FormData !== 'undefined' && body instanceof FormData;
}

/**
 * Parse an error response body into the unified `ApiErrorDetail`.
 * Accepts the new envelope `{ error: {...} }`, a bare detail, and the legacy
 * `{ success:false, message }` shape (normalized for parity).
 */
function parseErrorBody(raw: unknown): ApiErrorDetail {
  if (raw && typeof raw === 'object') {
    const obj = raw as { error?: unknown };
    // New unified envelope: { error: { code, message, details? } }
    const fromEnvelope = apiErrorDetailSchema.safeParse(obj.error);
    if (fromEnvelope.success) return fromEnvelope.data;
    // Bare detail (no envelope).
    const bare = apiErrorDetailSchema.safeParse(raw);
    if (bare.success) return bare.data;
    // Legacy `{ success:false, message }` — preserve the message.
    if (isLegacyErrorPayload(raw)) return normalizeLegacyError(raw).error;
    // An `{ error: {...} }`-shaped body the STRICT schema rejects: a non-standard
    // `code` (PK-QA-1's `CONFIRM_REQUIRED` 412) or a code that carries extra data
    // but no `message` (the pre-kickoff `VALIDATION_ERROR` 400, whose payload is
    // `fields`). `apiErrorDetailSchema` would discard both, but a downstream
    // caller needs the ORIGINAL body (e.g. `isConfirmRequired`, the missing-fields
    // reader), so preserve it verbatim under `details` and lift a human `message`
    // when present. `code` stays `INTERNAL` — it is never a KNOWN code here, and
    // must not be mistaken for an auth signal (no `handleUnauthorized`).
    if (obj.error && typeof obj.error === 'object') {
      const inner = obj.error as { message?: unknown };
      const message =
        typeof inner.message === 'string' && inner.message.length > 0
          ? inner.message
          : UNKNOWN_ERROR.message;
      return { code: 'INTERNAL', message, details: raw };
    }
  }
  return UNKNOWN_ERROR;
}

/**
 * Read and normalize a `Response` into `ApiResult<T>`.
 *  - 2xx → `ok(json)`: a bare ARRAY is returned as-is (a REST list — never run
 *    through `unwrapLegacySuccess`, which would collapse a 1-element array to an
 *    object); an OBJECT is passed through `unwrapLegacySuccess` (`{response}` /
 *    `{success:true,…}` envelopes collapsed). UNLESS the 2xx body is a legacy
 *    `{ success:false, message:'unauthorized' }`
 *    (the old n8n router answered the session-invalid case with HTTP 200), which
 *    is normalized to an `UNAUTHENTICATED` error and triggers handleUnauthorized;
 *  - 204 / empty → `ok({})`;
 *  - 401 → fires `handleUnauthorized` (clear session/cache + show login), then
 *    returns the typed error;
 *  - non-2xx → `err(parseErrorBody(json))`; an `UNAUTHENTICATED` detail also
 *    fires `handleUnauthorized`.
 * Never throws — a JSON-parse failure degrades to `UNKNOWN_ERROR`.
 *
 * Single choke-point for the unauthorized reaction (acceptance: "Любой ответ с
 * error='unauthorized' или HTTP 401 → handleUnauthorized"): every request flows
 * through here, so there is exactly one place that detects the signal.
 */
export async function toApiResult<T>(res: Response): Promise<ApiResult<T>> {
  // No-content responses (204, or empty body) → empty success object.
  if (res.status === 204) return ok({} as T);

  let raw: unknown;
  try {
    const text = await res.text();
    raw = text ? JSON.parse(text) : {};
  } catch {
    return res.ok ? ok({} as T) : err(UNKNOWN_ERROR);
  }

  if (res.ok) {
    // Legacy parity: the old router could return a session-invalid payload with
    // HTTP 200 (`{ success:false, message:'unauthorized' }`). Detect it, react,
    // and surface it as a normal unauthorized error rather than a fake success.
    if (isLegacyErrorPayload(raw)) {
      const detail = normalizeLegacyError(raw).error;
      if (isUnauthorizedError(detail)) handleUnauthorized();
      return err<T>(detail);
    }
    // A REST list endpoint returns a BARE array as its data — pass it through
    // untouched. `unwrapLegacySuccess` is for the legacy *object* envelopes only
    // (`{ response:{…} }`, `{ success:true, … }`); it must never see an array,
    // because it collapses a 1-element array `[{x}]` to the bare object `{x}`
    // (legacy.ts §"single-item array" parity). That collapse turned a list of
    // exactly ONE row into a non-iterable object → Dashboard'a `for…of` crash →
    // white screen (data-dependent: 0 and 2+ rows were fine). The live REST API
    // never emits the legacy `[{response}]` array envelope (every route does
    // `reply.send(<bare array | object>)`), so returning arrays as-is is safe.
    return ok((Array.isArray(raw) ? raw : unwrapLegacySuccess(raw)) as T);
  }

  const detail = parseErrorBody(raw);
  // HTTP 401 OR a normalized UNAUTHENTICATED detail → the single unauthorized
  // reaction: drop the stale session/cache and (if mounted) flip to login.
  if (res.status === 401 || isUnauthorizedError(detail)) {
    handleUnauthorized();
  } else if (isPasswordResetGate(res.status, detail)) {
    // 403 password_reset_required → drop the session ONCE and open the reset
    // view pre-filled with the user's email (not the login screen, not a
    // silent per-query error).
    handlePasswordResetRequired();
  }
  return err<T>(detail);
}

/** A non-empty-string `error`/`message` from an already-parsed envelope, if any. */
function pickErrorString(raw: unknown): string | undefined {
  if (raw && typeof raw === 'object') {
    const obj = raw as { error?: unknown; message?: unknown };
    if (typeof obj.error === 'string' && obj.error.length > 0) return obj.error;
    if (typeof obj.message === 'string' && obj.message.length > 0) return obj.message;
  }
  return undefined;
}

/**
 * Read a `Response` into the live-app `{ success, error?, ...data }` envelope
 * (acceptance E10-F01-T07 — the legacy `normalizeN8nPayload` contract the UI
 * consumes). This is the raw-`Response` companion to {@link normalizeN8nPayload}
 * (which only handles an already-parsed JSON value): it owns the HTTP-status and
 * non-JSON branches the JSON normalizer cannot see.
 *
 * Branches, matching the acceptance criteria exactly:
 *   1. non-JSON body (parse fails) → `{ success: response.ok, message: <raw text> }`;
 *   2. non-OK HTTP whose JSON body has no `success` field →
 *      `{ success:false, error: <message> || 'HTTP <status>' }`;
 *   3. otherwise → `normalizeN8nPayload(json)` (array / `{response}` / direct
 *      object all collapse to one shape);
 *   4. HTTP 401 in ANY branch → `error:'unauthorized'` is forced AND
 *      `handleUnauthorized()` fires (the single unauthorized reaction).
 *
 * Never throws — every failure mode degrades to a well-formed envelope.
 */
export async function responseToLegacyEnvelope(res: Response): Promise<LegacyEnvelope> {
  const text = await res.text().catch(() => '');

  let parsed: unknown;
  let isJson = false;
  if (text) {
    try {
      parsed = JSON.parse(text);
      isJson = true;
    } catch {
      isJson = false;
    }
  } else {
    // Empty body → treat as an empty success/failure object, not raw text.
    parsed = {};
    isJson = true;
  }

  let envelope: LegacyEnvelope;
  if (!isJson) {
    // (1) Non-JSON tail: wrap the raw text, success mirrors the HTTP status.
    envelope = { success: res.ok, message: text };
  } else if (!res.ok && pickErrorString(parsed) === undefined && !isLegacyErrorPayload(parsed)) {
    // (2) Non-OK HTTP with no top-level success/error string. The body may still
    //     carry a real message in the unified envelope (`{ error:{ message } }`),
    //     which `pickErrorString` cannot see — so consult `parseErrorBody`. But
    //     guard against its synthetic `UNKNOWN_ERROR` fallback ('Unexpected
    //     error'): when no real detail was found, fall back to `HTTP <status>`
    //     instead of letting the truthy placeholder mask the status.
    const detail = parseErrorBody(parsed);
    const realMessage = detail === UNKNOWN_ERROR ? undefined : detail.message;
    envelope = { success: false, error: realMessage || `HTTP ${res.status}` };
  } else {
    // (3) Well-formed JSON in any n8n shape → the unified envelope.
    envelope = normalizeN8nPayload(parsed);
  }

  // (4) 401 in ANY branch → force the unauthorized signal + the single reaction.
  if (res.status === 401) {
    handleUnauthorized();
    return { ...envelope, success: false, error: 'unauthorized' };
  }
  // 403 password_reset_required (E12-F08-T13) → the same single reset reaction.
  if (res.status === 403 && isJson && isPasswordResetGate(403, parseErrorBody(parsed))) {
    handlePasswordResetRequired();
  }
  // A body that itself signals unauthorized (legacy session-invalid node answered
  // HTTP 200 with `{ success:false, message:'unauthorized' }`) also reacts.
  if (
    typeof envelope.error === 'string' &&
    envelope.error.trim().toLowerCase() === 'unauthorized'
  ) {
    handleUnauthorized();
  }
  return envelope;
}

/**
 * Core request. Auto-injects `session_token`, serialises the body (JSON or
 * multipart), and returns a normalized `ApiResult<T>`. A network failure is
 * caught and returned as `{ ok:false, error }` — it never rejects.
 */
export async function request<T>(path: string, opts: RequestOptions = {}): Promise<ApiResult<T>> {
  const { method = 'GET', body, query, headers = {}, signal, command, keepalive } = opts;
  const token = getSessionToken();

  const finalHeaders: Record<string, string> = { Accept: 'application/json', ...headers };
  if (token) finalHeaders.Authorization = `Bearer ${token}`;
  if (command) finalHeaders['X-EER-Command'] = command;

  let payload: BodyInit | undefined;
  if (body !== undefined && method !== 'GET') {
    if (isFormData(body)) {
      // Multipart: append command + session_token INTO the form (legacy parity).
      // Do NOT set Content-Type — the browser sets the multipart boundary.
      if (command && !body.has('command')) body.append('command', command);
      if (token && !body.has('session_token')) body.append('session_token', token);
      payload = body;
    } else {
      finalHeaders['Content-Type'] = 'application/json';
      payload = JSON.stringify(body);
    }
  }

  try {
    const res = await fetch(buildUrl(path, query), {
      method,
      headers: finalHeaders,
      body: payload,
      signal,
      ...(keepalive ? { keepalive: true } : {}),
    });
    return await toApiResult<T>(res);
  } catch (cause) {
    // AbortError → surface as a network-style error; callers (TanStack Query)
    // treat an aborted query as cancellation upstream of this value anyway.
    if (cause instanceof DOMException && cause.name === 'AbortError') {
      return err<T>({ code: 'INTERNAL', message: 'Request aborted' });
    }
    return err<T>(NETWORK_ERROR);
  }
}

/**
 * Ergonomic typed client. Each verb returns `ApiResult<T>` and never throws.
 * `upload` is the multipart entry point (FormData), mirroring the legacy
 * file-upload `apiCall` that appended `command` + `session_token` to the form.
 */
export const apiClient = {
  get<T>(path: string, opts?: Omit<RequestOptions, 'method' | 'body'>): Promise<ApiResult<T>> {
    return request<T>(path, { ...opts, method: 'GET' });
  },
  post<T>(
    path: string,
    body?: unknown,
    opts?: Omit<RequestOptions, 'method' | 'body'>,
  ): Promise<ApiResult<T>> {
    return request<T>(path, { ...opts, method: 'POST', body });
  },
  patch<T>(
    path: string,
    body?: unknown,
    opts?: Omit<RequestOptions, 'method' | 'body'>,
  ): Promise<ApiResult<T>> {
    return request<T>(path, { ...opts, method: 'PATCH', body });
  },
  put<T>(
    path: string,
    body?: unknown,
    opts?: Omit<RequestOptions, 'method' | 'body'>,
  ): Promise<ApiResult<T>> {
    return request<T>(path, { ...opts, method: 'PUT', body });
  },
  delete<T>(path: string, opts?: Omit<RequestOptions, 'method' | 'body'>): Promise<ApiResult<T>> {
    return request<T>(path, { ...opts, method: 'DELETE' });
  },
  /** Multipart upload (FormData). Command + session_token are appended to the form. */
  upload<T>(
    path: string,
    form: FormData,
    opts?: Omit<RequestOptions, 'method' | 'body'>,
  ): Promise<ApiResult<T>> {
    return request<T>(path, { ...opts, method: 'POST', body: form });
  },
} as const;

/**
 * Unwrap an `ApiResult` for the TanStack Query `queryFn`/`mutationFn` boundary:
 * Query needs a *thrown* error to mark a query failed, but the rest of the app
 * must never see a throw — so the throw lives ONLY here, at that boundary, and
 * carries the typed `ApiErrorDetail` so `onError` can read `error.message`.
 */
export class ApiResultError extends Error {
  readonly detail: ApiErrorDetail;
  constructor(detail: ApiErrorDetail) {
    super(detail.message);
    this.name = 'ApiResultError';
    this.detail = detail;
  }
}

/** Throw on failure (for query/mutation fns), return data on success. */
export function unwrap<T>(result: ApiResult<T>): T {
  if (result.ok) return result.data;
  throw new ApiResultError(result.error);
}

/**
 * Safely read a human message out of ANY query/mutation error without assuming
 * its shape. A query can fail two ways:
 *   - the api-client returned `{ ok:false }` → `unwrap` throws an `ApiResultError`
 *     (has `.detail.message`);
 *   - the response body failed schema validation → a `ZodError` (or any `Error`)
 *     is thrown, which has NO `.detail`.
 * Reading `error.detail.message` blindly crashes on the second case (this is what
 * white-screened the admin Settings view), so every tab MUST funnel its error
 * through this extractor instead of touching `.detail` directly.
 */
export function errorMessage(error: unknown, fallback = 'Something went wrong'): string {
  if (error instanceof ApiResultError) return error.detail.message || fallback;
  if (error && typeof error === 'object') {
    const obj = error as { detail?: { message?: unknown }; message?: unknown };
    if (obj.detail && typeof obj.detail.message === 'string' && obj.detail.message) {
      return obj.detail.message;
    }
    if (typeof obj.message === 'string' && obj.message) return obj.message;
  }
  return fallback;
}

/**
 * The RAW error body preserved on a failure the strict error schema could not
 * classify — the PK-QA-1 `CONFIRM_REQUIRED` 412 and the pre-kickoff
 * `VALIDATION_ERROR` 400 (see {@link parseErrorBody}). Returns the original
 * `{ error: {...} }` body so a caller can re-parse it with a specific guard
 * (`isConfirmRequired`, the missing-fields reader), or `undefined` for a
 * normally-classified error / a non-`ApiResultError` throw. Never throws.
 */
export function errorBodyOf(error: unknown): unknown {
  return error instanceof ApiResultError ? error.detail.details : undefined;
}

export { API_BASE_URL };
